import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';

// ativos
import { SearchOutlined } from '@ant-design/icons';

// importação do projeto
import { ThemeMode } from '../../../../../config';
import { membrosEquipe, corDoAvatar, obterIniciais } from './mockChat';

// ==============================|| CHAT - LISTA DE CONVERSAS ||============================== //

const pontoStatus = (theme, status) => {
  if (status === 'online') return theme.palette.success.main;
  if (status === 'ausente') return theme.palette.warning.main;
  return theme.palette.grey[400];
};

const ListaConversas = ({ conversas, idSelecionado, onSelecionar, larguraCheia }) => {
  const theme = useTheme();
  const modoEscuro = theme.palette.mode === ThemeMode.DARK;
  const [busca, setBusca] = useState('');

  const itens = useMemo(
    () =>
      conversas
        .map((conversa) => {
          const membro = membrosEquipe.find((m) => m.id === conversa.idMembro);
          const ultima = conversa.mensagens[conversa.mensagens.length - 1];
          return { ...conversa, membro, ultima };
        })
        .filter(({ membro }) => membro?.nome.toLowerCase().includes(busca.trim().toLowerCase())),
    [conversas, busca]
  );

  return (
    <Stack
      sx={{
        height: '100%',
        minHeight: 0,
        width: larguraCheia ? '100%' : 300,
        flexShrink: 0,
        borderRight: larguraCheia ? 'none' : `1px solid ${theme.palette.divider}`
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <OutlinedInput
          fullWidth
          size="small"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Pesquisar na equipe"
          startAdornment={
            <InputAdornment position="start">
              <SearchOutlined style={{ fontSize: '0.9rem', color: theme.palette.text.secondary }} />
            </InputAdornment>
          }
          sx={{ borderRadius: 5, bgcolor: modoEscuro ? 'background.default' : 'grey.50', '& input': { fontSize: '0.8125rem' } }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {itens.length === 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            Nenhum membro encontrado.
          </Typography>
        )}
        <List component="nav" sx={{ p: 0 }}>
          {itens.map(({ id, membro, ultima, naoLidas }) => {
            const cor = corDoAvatar(theme, membro.id);
            const selecionado = id === idSelecionado;
            return (
              <ListItemButton
                key={id}
                selected={selecionado}
                onClick={() => onSelecionar(id)}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  '&.Mui-selected': {
                    bgcolor: modoEscuro ? alpha(theme.palette.primary.main, 0.18) : theme.palette.primary.lighter,
                    '&:hover': { bgcolor: modoEscuro ? alpha(theme.palette.primary.main, 0.24) : theme.palette.primary.lighter }
                  }
                }}
              >
                <ListItemAvatar sx={{ minWidth: 48 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: pontoStatus(theme, membro.status),
                          border: `2px solid ${theme.palette.background.paper}`
                        }}
                      />
                    }
                  >
                    <Avatar sx={{ width: 38, height: 38, fontSize: '0.8125rem', color: cor.main, bgcolor: cor.lighter }}>
                      {obterIniciais(membro.nome)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  sx={{ my: 0 }}
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                        {membro.nome}
                      </Typography>
                      <Typography variant="caption" color={naoLidas > 0 ? 'primary' : 'textSecondary'} sx={{ flexShrink: 0 }}>
                        {ultima?.hora}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        noWrap
                        sx={{ flexGrow: 1, minWidth: 0, fontWeight: naoLidas > 0 ? 600 : 400 }}
                      >
                        {ultima?.remetente === 'eu' ? 'Você: ' : ''}
                        {ultima?.texto}
                      </Typography>
                      {naoLidas > 0 && (
                        <Box
                          sx={{
                            flexShrink: 0,
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 9,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            lineHeight: '18px',
                            textAlign: 'center'
                          }}
                        >
                          {naoLidas}
                        </Box>
                      )}
                    </Stack>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Stack>
  );
};

ListaConversas.propTypes = {
  conversas: PropTypes.array,
  idSelecionado: PropTypes.number,
  onSelecionar: PropTypes.func,
  larguraCheia: PropTypes.bool
};

export default ListaConversas;
